import { arrayMove } from '@dnd-kit/sortable';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import type { IconComponent, PopoverContextProps } from '@iheartjane/reefer';
import {
  Button,
  Flex,
  EyeHideIcon,
  List,
  Modal,
  MoreHorizontalIcon,
  Popover,
  Skeleton,
  SparkleIcon,
  Typography,
  useToast,
} from '@iheartjane/reefer';
import {
  Form,
  FormValidationError,
  useForm,
} from '@iheartjane/reefer-hook-form';
import type { UseMutateAsyncFunction } from '@tanstack/react-query';
import type { FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import { ConfirmDisableModal } from '@jane/business-admin/components/ConfirmDisableModal';
import { ConfirmWrapperWithTracking } from '@jane/business-admin/components/ConfirmWrapperWithTracking';
import {
  useStoreMenu,
  useStoreSettings,
} from '@jane/business-admin/data-access';
import {
  useCatchErrorsWithManager,
  useStoreId,
} from '@jane/business-admin/hooks';
import type { MenuRow, MenuTab } from '@jane/business-admin/types';
import type {
  EditedStoreMenuConfig,
  ModalNames,
  SettingNames,
} from '@jane/business-admin/util';
import {
  ErrorReasons,
  EventNames,
  normalizePath,
  parseValidationErrors,
  track,
} from '@jane/business-admin/util';
import { SortableList } from '@jane/shared/components';
import type { SortableItem } from '@jane/shared/components';

import { getRowLabel, getTabLabel } from './landing/utils';

const FORM_ERROR_NAME = 'menu-row-arrange-errors';

type ArrangeModalType = 'row' | 'tab';

type ArrangeRowProps<MenuItem> = Pick<
  ArrangeModalProps<MenuItem, []>,
  | 'allowDisablingForItem'
  | 'allowRearrangingForItem'
  | 'enabledKey'
  | 'getItemCountLabel'
  | 'getItemEnabled'
  | 'getItemId'
  | 'rankKey'
  | 'showHiddenIconForItem'
  | 'type'
> & {
  hasEmbeddedRow: boolean;
  item: MenuItem;
  itemIcon?: FC;
  itemLabel: string;
  modalName: ModalNames;
  moveToBottom: (
    item: MenuItem,
    modalName: ModalNames,
    settingName: SettingNames
  ) => void;
  moveToTop: (
    item: MenuItem,
    modalName: ModalNames,
    settingName: SettingNames
  ) => void;
  rankKey: keyof MenuItem;
  rowEnabledChanged: (id: string, enabled: boolean) => void;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  settingName: SettingNames;
};

const ArrangeRow = <MenuItem,>({
  allowDisablingForItem,
  allowRearrangingForItem,
  enabledKey,
  getItemCountLabel,
  getItemEnabled,
  getItemId,
  hasEmbeddedRow,
  item,
  itemIcon,
  itemLabel,
  modalName,
  moveToBottom,
  moveToTop,
  rankKey,
  rowEnabledChanged,
  scrollContainerRef,
  settingName,
  showHiddenIconForItem,
  type,
}: ArrangeRowProps<MenuItem>) => {
  const handleMoveToTop = (closePopover: () => void) => {
    moveToTop(item, modalName, settingName);

    closePopover();
  };

  const handleMoveToBottom = (closePopover: () => void) => {
    moveToBottom(item, modalName, settingName);

    closePopover();
  };

  const handleToggleVisibility = (enabled: boolean) => {
    rowEnabledChanged(getItemId(item), enabled);

    track({
      event: EventNames.ToggleVisibility,
      final_state: enabled ? 'visible' : 'hidden',
      trigger_source_id: 'arrange modal',
      successful: true,
      object: `menu ${type}`,
    });
  };

  const Icon = itemIcon as IconComponent;
  const itemCountLabel = getItemCountLabel && getItemCountLabel(item);
  const allowRearraging = allowRearrangingForItem
    ? allowRearrangingForItem(item)
    : true;
  const enabled = getItemEnabled
    ? getItemEnabled(item)
    : (item[enabledKey] as boolean);

  const [openConfirmDisableModal, setOpenConfirmDisableModal] = useState(false);
  const [popoverVertical, setPopoverVertical] = useState<'top' | 'bottom'>(
    'bottom'
  );
  const rowRef = useRef<HTMLDivElement>(null);
  const isJaneGoldRow = itemLabel.includes('Jane Gold');
  const confirmDisable = isJaneGoldRow || hasEmbeddedRow;

  const handlePopoverOpen = () => {
    if (rowRef.current && scrollContainerRef.current) {
      const rowRect = rowRef.current.getBoundingClientRect();
      const containerRect = scrollContainerRef.current.getBoundingClientRect();
      const isInBottomHalf =
        rowRect.top > containerRect.top + containerRect.height / 2;
      setPopoverVertical(isInBottomHalf ? 'top' : 'bottom');
    }
  };

  return (
    <Flex
      ref={rowRef}
      data-nonworking-example
      width="100%"
      justifyContent="space-between"
    >
      <Flex alignItems="center">
        {itemIcon && <Icon />}
        <Typography mr={16}>{itemLabel}</Typography>
        {showHiddenIconForItem(item) ? <EyeHideIcon /> : null}
      </Flex>
      <Flex flexDirection="row" gap={16} alignItems="center">
        {itemCountLabel && <Typography>{itemCountLabel}</Typography>}
        {allowDisablingForItem(item) && (
          <>
            <Form.SwitchField
              data-testid={`enabled-checkbox-${getItemId(item)}`}
              labelHidden
              label="visible"
              name={`enabled-${getItemId(item)}`}
              {...(confirmDisable
                ? {
                    checked: enabled,
                    onClick: (evt) => {
                      if (item[enabledKey] as boolean) {
                        setOpenConfirmDisableModal(true);
                        evt.preventDefault();
                      } else {
                        handleToggleVisibility(true);
                      }
                    },
                  }
                : {
                    defaultChecked: enabled,
                    onChange: handleToggleVisibility,
                  })}
            />
            <ConfirmDisableModal
              itemName={isJaneGoldRow ? type : itemLabel}
              type={isJaneGoldRow ? 'gold' : 'embedded'}
              open={openConfirmDisableModal}
              onCancel={() => setOpenConfirmDisableModal(false)}
              onConfirm={() => {
                setOpenConfirmDisableModal(false);
                handleToggleVisibility(false);
              }}
            />
          </>
        )}
        {allowRearraging && (
          <Popover
            disableMobileStyling
            target={<Button.Icon icon={<MoreHorizontalIcon />} />}
            openOn="click"
            alignment={{
              horizontal: 'right',
              vertical: popoverVertical,
            }}
            onOpen={handlePopoverOpen}
            label="more options"
            closeOnTargetClick
          >
            {({ closePopover }: PopoverContextProps) => (
              <Popover.Content>
                <List label="more options items">
                  <List.Item
                    ariaLabel="Bring to top"
                    onClick={
                      (item[rankKey] as number) > 0
                        ? () => handleMoveToTop(closePopover)
                        : undefined
                    }
                  >
                    <Typography
                      color={
                        (item[rankKey] as number) > 0
                          ? 'grays-black'
                          : 'grays-light'
                      }
                    >
                      Bring to top
                    </Typography>
                  </List.Item>
                  <List.Item
                    ariaLabel="Bring to bottom"
                    onClick={() => handleMoveToBottom(closePopover)}
                  >
                    <Typography>Bring to bottom</Typography>
                  </List.Item>
                </List>
              </Popover.Content>
            )}
          </Popover>
        )}
      </Flex>
    </Flex>
  );
};

interface ArrangeModalProps<MenuItem, UpdateBody extends Array<unknown>> {
  allowDisablingForItem: (item: MenuItem) => boolean;
  allowRearrangingForItem?: (item: MenuItem) => boolean;
  closeModal: () => void;
  enabledKey: keyof MenuItem;
  filterMenuItems?: (items: MenuItem[]) => MenuItem[];
  formatItem?: (item: { id: string; item: MenuItem }) => UpdateBody[number];
  getItemCountLabel?: (item: MenuItem) => string;
  getItemEnabled?: (item: MenuItem) => boolean;
  getItemId: (item: MenuItem) => string;
  menuDataKey: 'menu_rows' | 'menu_tabs';
  modalName: ModalNames;
  rankKey: keyof MenuItem;
  settingName: SettingNames;
  showHiddenIconForItem: (item: MenuItem) => boolean;
  title?: string;
  type: ArrangeModalType;
  updateMenuOrder: UseMutateAsyncFunction<null, unknown, UpdateBody, unknown>;
  updateMenuOrderSuccess: boolean;
}

export const ArrangeModal = <MenuItem, UpdateBody extends Array<unknown>>({
  allowDisablingForItem,
  allowRearrangingForItem,
  closeModal,
  enabledKey,
  filterMenuItems,
  formatItem,
  getItemCountLabel,
  getItemEnabled,
  getItemId,
  menuDataKey,
  modalName,
  rankKey,
  settingName,
  showHiddenIconForItem,
  title,
  type,
  updateMenuOrder,
  updateMenuOrderSuccess,
}: ArrangeModalProps<MenuItem, UpdateBody>) => {
  const thematicRows = useFlag(FLAGS.comm3MultipleThematicRows);
  const storeId = useStoreId();
  const { pathname } = useLocation();
  const { data: storePayload, isFetching: storeSettingsLoading } =
    useStoreSettings(storeId);
  const { data: menuData, isFetched } = useStoreMenu(storeId);
  const formMethods = useForm();
  const [sortedMenuItems, setSortedMenuItems] = useState<MenuItem[]>([]);
  const originalRowData = useRef<MenuItem[]>([]);
  const catchSubmitErrors = useCatchErrorsWithManager(
    `Error updating menu ${type} order. Please try again.`
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const {
    formState: { isDirty, dirtyFields },
  } = formMethods;

  useEffect(() => {
    if (isFetched && !!menuData) {
      originalRowData.current = [
        // Seems ridiculous, but otherwise array objects get modified downstream and we can't revert to original data when changes are discarded
        ...JSON.parse(JSON.stringify(menuData[menuDataKey])),
      ];
      setSortedMenuItems(originalRowData.current);
    } else {
      setSortedMenuItems([]);
    }
  }, [isFetched, JSON.stringify(menuData)]);

  const onCloseDiscardChanges = () => {
    setSortedMenuItems(originalRowData.current || []);
  };

  const onReorder = (items: SortableItem<MenuItem>[]) => {
    const reformat = items.map(({ item }) => item!);
    setSortedMenuItems(reformat);
    track({
      action: 'dragged',
      event: EventNames.ModifiedSetting,
      revert: false,
      setting_name: settingName,
      modal_name: modalName,
    });
  };

  useEffect(() => {
    if (updateMenuOrderSuccess) {
      toast.add({
        label: `Menu ${type} order updated.`,
        variant: 'success',
      });
      closeModal();
    }
  }, [updateMenuOrderSuccess]);

  const onSubmit = () => {
    const formattedData = sortedMenuItems.map((item, index) => {
      (item[rankKey] as number) = index;
      const entry = { id: getItemId(item), item };
      return formatItem ? formatItem(entry) : entry;
    }) as unknown as UpdateBody;

    const changedKeys = Object.keys(dirtyFields);
    if (hasChangedOrder) {
      changedKeys.push('menuRowOrder');
    }

    const trackSubmit = (eventProps: Partial<EditedStoreMenuConfig> = {}) => {
      track({
        event: EventNames.EditedStoreMenuConfig,
        action: 'update',
        changed_attributes: changedKeys,
        modal_name: modalName,
        setting_name: settingName,
        successful: true,
        url: normalizePath(pathname, storeId),
        ...eventProps,
      });
    };

    return catchSubmitErrors({
      submitMethod: async () => {
        await updateMenuOrder(formattedData);
        trackSubmit();
      },
      requestData: formattedData,
      onValidationError: (validationErrors: Record<string, unknown>) => {
        trackSubmit({
          successful: false,
          error_reason: ErrorReasons.InvalidParams,
        });
        throw new FormValidationError(
          FORM_ERROR_NAME,
          parseValidationErrors(validationErrors)
        );
      },
      callback: () => {
        trackSubmit({
          successful: false,
          error_reason: ErrorReasons.ServerError,
        });
        throw new Error(`Error updating menu ${type} order. Please try again.`);
      },
    });
  };

  const moveToTop = (
    item: MenuItem,
    modalName: ModalNames,
    settingName: SettingNames
  ) => {
    // Get current index in array rather than using ranking, as
    // ranking does not take into account disabled items in the array
    const currentIndex = sortedMenuItems.indexOf(item);

    // Find the first item in list that can be rearranged
    const firstAvailableSlot = sortedMenuItems.find((item) =>
      allowRearrangingForItem ? allowRearrangingForItem(item) : true
    );
    const newSlot = firstAvailableSlot
      ? sortedMenuItems.indexOf(firstAvailableSlot)
      : 0;
    const newOrder = arrayMove(sortedMenuItems, currentIndex, newSlot);
    setSortedMenuItems(newOrder);
    track({
      action: 'bring to top',
      event: EventNames.ModifiedSetting,
      setting_name: settingName,
      revert: false,
      modal_name: modalName,
    });
  };

  const moveToBottom = (
    item: MenuItem,
    modalName: ModalNames,
    settingName: SettingNames
  ) => {
    // Get current index in array rather than using ranking, as
    // ranking does not take into account disabled items in the array
    const currentIndex = sortedMenuItems.indexOf(item);
    const newIndex = sortedMenuItems.length - 1;
    const newOrder = arrayMove(sortedMenuItems, currentIndex, newIndex);
    setSortedMenuItems(newOrder);
    track({
      action: 'bring to bottom',
      event: EventNames.ModifiedSetting,
      setting_name: settingName,
      revert: false,
      modal_name: modalName,
    });
  };

  const changeRowEnabled = (id: string, enabled: boolean) => {
    setSortedMenuItems((items) =>
      items.map((item) =>
        getItemId(item) === id ? { ...item, [enabledKey]: enabled } : item
      )
    );
  };

  const filteredDisplayMenuItems = useMemo(() => {
    return filterMenuItems ? filterMenuItems(sortedMenuItems) : sortedMenuItems;
  }, [filterMenuItems, sortedMenuItems]);

  const sortedDisplayMenuItems = useMemo(() => {
    return filteredDisplayMenuItems.map((item) => ({
      id: getItemId(item),
      item,
    }));
  }, [JSON.stringify(filteredDisplayMenuItems)]);

  const hasChangedOrder = useMemo(() => {
    if (isFetched && !!menuData) {
      return (
        JSON.stringify(sortedMenuItems) !==
        JSON.stringify(menuData[menuDataKey])
      );
    }
    return false;
  }, [isFetched, menuData, sortedMenuItems]);

  return (
    <ConfirmWrapperWithTracking
      open
      setOpen={closeModal}
      variant="standard"
      hasChanges={isDirty || hasChangedOrder}
      modalName={modalName}
      onCloseDiscardChanges={onCloseDiscardChanges}
    >
      <Form.BaseForm
        name={`arrange menu ${type}s form`}
        onSubmit={onSubmit}
        formMethods={formMethods}
        formErrorName={FORM_ERROR_NAME}
      >
        <Modal.Header
          title={title || `Arrange menu ${type}s`}
          subtitle={storeSettingsLoading ? '' : storePayload?.store.name}
          actions={
            <Form.SubmitButton variant="primary" label="Publish" ml={16} />
          }
        />
        <Modal.Content ref={scrollContainerRef}>
          <Form.ErrorBanner name={FORM_ERROR_NAME} />
          {isFetched && menuData && sortedDisplayMenuItems.length ? (
            <SortableList
              disabledForItem={
                allowRearrangingForItem
                  ? ({ item }) => !allowRearrangingForItem(item!)
                  : undefined
              }
              direction="vertical"
              items={sortedDisplayMenuItems}
              onChange={onReorder}
              renderItem={({ item }) => (
                <ArrangeRow<MenuItem>
                  allowDisablingForItem={allowDisablingForItem}
                  allowRearrangingForItem={allowRearrangingForItem}
                  enabledKey={enabledKey}
                  getItemCountLabel={getItemCountLabel}
                  getItemEnabled={getItemEnabled}
                  getItemId={getItemId}
                  hasEmbeddedRow={!!(item as MenuRow).embedded_menu_row}
                  modalName={modalName}
                  moveToTop={moveToTop}
                  item={item!}
                  itemIcon={
                    thematicRows &&
                    (item as MenuRow).menu_row_type === 'magic_row'
                      ? () => <SparkleIcon size="sm" mr={8} color="primary" />
                      : undefined
                  }
                  itemLabel={
                    type === 'row'
                      ? getRowLabel(
                          item as MenuRow,
                          menuData.filters_and_labels?.custom_labels,
                          thematicRows
                        )
                      : getTabLabel(
                          item as MenuTab,
                          menuData.filters_and_labels?.custom_labels
                        )
                  }
                  moveToBottom={moveToBottom}
                  rowEnabledChanged={changeRowEnabled}
                  scrollContainerRef={scrollContainerRef}
                  rankKey={rankKey}
                  settingName={settingName}
                  showHiddenIconForItem={showHiddenIconForItem}
                  type={type}
                />
              )}
            />
          ) : (
            <ArrangeModalSkeleton />
          )}
        </Modal.Content>
      </Form.BaseForm>
    </ConfirmWrapperWithTracking>
  );
};

function ArrangeModalSkeleton() {
  return (
    <Skeleton direction="column" animate>
      {Array.from({ length: 10 }, (_, i) => (
        <Skeleton.Bone
          key={`skeleton-${i}`}
          mb={24}
          height="48px"
          width="100%"
          borderRadius="sm"
        />
      ))}
    </Skeleton>
  );
}
